/**
 * Block Normalizer
 *
 * Handles Anthropic skeleton schema responses where blocks, content,
 * and behaviours may arrive as stringified JSON instead of proper objects.
 *
 * With the skeleton schema, only `version` and `blocks` (as an untyped array)
 * are schema-enforced. Inner fields like `content`, `behaviours`, `config`,
 * and nested `blocks` (in containers) may be stringified.
 *
 * Works transparently with OpenAI responses — properly structured JSON
 * passes through unchanged (typeof checks are O(1)).
 */

import type { UIBlock, ContainerBlock, RenderPayload } from '@/types/ui-blocks';

/**
 * Safely attempt to parse a value if it's a JSON string.
 * Returns the parsed value, or the original value if it's not a string
 * or if parsing fails.
 */
function tryParseJsonString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * Normalize a single block: parse stringified content, behaviours, and config fields.
 *
 * - If the block itself is a string, parse it first (handles stringified array items).
 * - Skips `content` parsing for `type: "text"` blocks (content is plain markdown).
 * - Recurses into container blocks to normalize nested blocks.
 */
export function normalizeBlock(block: unknown): UIBlock | ContainerBlock {
  // If the block itself is a string, parse it first
  const resolved = typeof block === 'string' ? tryParseJsonString(block) : block;

  if (typeof resolved !== 'object' || resolved === null) {
    // Return as-is; the renderer's BlockErrorBoundary will handle the error
    return resolved as UIBlock;
  }

  const obj = { ...resolved } as Record<string, unknown>;

  // Normalize 'content' field (may be stringified for charts, tables, etc.)
  // Skip for text blocks — content is always a plain markdown string
  // Delete null content on non-text blocks (malformed LLM output) so renderers
  // can rely on truthiness checks instead of explicit null guards everywhere.
  if (obj.content === null && obj.type !== 'text') {
    delete obj.content;
  } else if (typeof obj.content === 'string' && obj.type !== 'text') {
    obj.content = tryParseJsonString(obj.content);
  }

  // Normalize 'behaviours' field (may be a stringified array or null from malformed LLM output)
  if (obj.behaviours === null) {
    delete obj.behaviours;
  } else if (typeof obj.behaviours === 'string') {
    obj.behaviours = tryParseJsonString(obj.behaviours);
  }

  // Normalize nested fields inside each behaviour:
  // - action.data may be stringified (Anthropic schema constraint)
  // - action.data.blocks may contain blocks with stringified content (double-stringify from nesting)
  if (Array.isArray(obj.behaviours)) {
    obj.behaviours = (obj.behaviours as Record<string, unknown>[]).map(behaviour => {
      const action = behaviour.action as Record<string, unknown> | undefined;
      if (!action) return behaviour;

      let data = action.data;
      // Parse stringified data
      if (typeof data === 'string') {
        data = tryParseJsonString(data);
      }

      // Normalize blocks inside action.data (e.g., report blocks passed to insight_preview)
      if (typeof data === 'object' && data !== null) {
        const dataObj = data as Record<string, unknown>;
        if (Array.isArray(dataObj.blocks)) {
          dataObj.blocks = (dataObj.blocks as unknown[]).map(normalizeBlock);
          data = dataObj;
        }
      }

      if (data !== action.data) {
        return { ...behaviour, action: { ...action, data } };
      }
      return behaviour;
    });
  }

  // Normalize 'config' field (used in some block types)
  if (typeof obj.config === 'string') {
    obj.config = tryParseJsonString(obj.config);
  }

  // Recurse into container blocks
  if (obj.type === 'container' && obj.blocks != null) {
    if (typeof obj.blocks === 'string') {
      obj.blocks = tryParseJsonString(obj.blocks);
    }
    if (Array.isArray(obj.blocks)) {
      obj.blocks = obj.blocks.map(normalizeBlock);
    }
  }

  return obj as UIBlock | ContainerBlock;
}

/**
 * Normalize an entire blocks array.
 * Filters out malformed entries that lack a `type` field — these are stray
 * JSON fragments from corrupted LLM output (e.g., `{" version":"v1"}`).
 */
export function normalizeBlocks(blocks: unknown[]): (UIBlock | ContainerBlock)[] {
  return blocks
    .map(normalizeBlock)
    .filter((block) => {
      // Every valid block must have a string `type` field
      if (typeof block !== 'object' || block === null) return false;
      return typeof (block as Record<string, unknown>).type === 'string';
    });
}

/**
 * Normalize a full RenderPayload: parse stringified blocks, content, behaviours.
 */
export function normalizePayload(payload: RenderPayload): RenderPayload {
  return {
    ...payload,
    blocks: normalizeBlocks(payload.blocks),
  };
}
